import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next'; // Add this import
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import ClassNames from 'embla-carousel-class-names';
import aboutbg from './../../images/aboutus-bg-3.png';
import { motion } from 'framer-motion';

function useCountAnimation(target: number, duration = 1000) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let rafId: number;
    let start: number | null = null;

    const step = (timestamp: number) => {
      if (start === null) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const current = Math.floor(progress * target);
      setValue(current);
      if (progress < 1) rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);

  return value;
}

function formatNumberWithSpaces(n: number) {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

const DotButton = ({
  selected,
  onClick,
}: {
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    className={`embla__dot ${selected ? 'is-selected' : ''}`}
    type="button"
    onClick={onClick}
  />
);

export const AboutScreen = () => {
  const { t } = useTranslation(); // Hook for translations
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 3000 }),
    ClassNames(),
  ]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    });
  }, [emblaApi]);

  const countCustomerSatisfaction = useCountAnimation(100, 1000);
  const countEnergyEfficiency = useCountAnimation(95, 1000);
  const countBlindsInstalled = useCountAnimation(10000, 1000);

  return (
    <>
      <Helmet>
        <title>Sonnen Raffstoren GmbH — Über uns</title>
        <meta
          name="description"
          content="Sonnen Raffstoren GmbH ist Ihr Spezialist für hochwertige Raffstoren, Rollläden und maßgeschneiderte Außenbeschattung für Wohn- und Gewerbeimmobilien. Wir verbinden langlebige Materialien, präzise Verarbeitung und moderne Technik, um zuverlässigen Sonnen-, Blend- und Sichtschutz zu schaffen und gleichzeitig Energieeffizienz sowie Wohnkomfort zu steigern. Von der Beratung und Vor-Ort-Messung über die individuelle Planung bis zur fachgerechten Montage, Inbetriebnahme und Wartung begleiten wir Projekte transparent und termingerecht. Wählen Sie Lamellenprofile, Farben und Oberflächen passend zur Architektur; optional integrieren wir windstabile Konstruktionen, wetterfeste Führungsschienen und Insektenschutz. Elektrische Antriebe, Wind- und Sonnensensoren sowie smarte Steuerungen ermöglichen automatisierte Abläufe und die Integration in Smart-Home- und Gebäudeautomationssysteme. Unser Qualitätsverständnis basiert auf geprüften Materialien, Korrosionsschutz, strengen Funktionsprüfungen und geschulten Teams. Nachhaltige Beschattungsstrategien helfen, sommerliche Überhitzung zu reduzieren, Tageslicht sinnvoll zu nutzen und den Bedarf an Klimatisierung zu senken."
        />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content="Sonnen Raffstoren GmbH — Über uns" />
        <meta
          property="og:description"
          content="Premium Raffstoren, Rollläden und Außenbeschattung: Beratung, Vor-Ort-Messung, Montage, Smart-Steuerung, Wartung und nachhaltige Energieeffizienz."
        />
        <meta property="og:type" content="website" />

        <html style={{ display: 'none' }}>
          {`
          <h1>
            Raffstoren — Hochwertige Außenjalousien für präzise Lichtsteuerung
          </h1>
          Raffstoren sind außenliegende Jalousien mit verstellbaren Lamellen,
          die eine präzise Regulierung von Licht, Wärme und Sichtschutz
          ermöglichen. Sie bieten optimalen Schutz vor direkter
          Sonneneinstrahlung, reduzieren Blendung und tragen wesentlich zur
          Energieeffizienz bei, indem sie den Kühlbedarf in Gebäuden senken.
          <h2>
            Markisen — Elegante Beschattung für Terrassen und Außenbereiche
          </h2>
          Markisen schaffen flexible Sonnenschutzlösungen für Balkone, Terrassen
          und Außenflächen. Mit verschiedenen Größen, Farben und hochwertigen
          Materialien passen sie sich perfekt an jede Architektur an und bieten
          komfortablen Schutz vor Hitze und UV-Strahlen bei gleichzeitiger
          Nutzung des Außenbereichs.
          <h3>Insektenschutz — Zuverlässiger Schutz vor Insekten</h3>
          Insektenschutzsysteme halten unerwünschte Insekten effektiv draußen,
          während sie gleichzeitig frische Luft hereinlassen. Sie können nahtlos
          mit Raffstoren und Markisen kombiniert werden und bieten ganzjährigen
          Komfort sowie Schutz vor Mücken, Fliegen und anderen Insekten.
          <h1>Rollläden — Sicherheits- und Isolationslösungen</h1>
          Rollläden bieten nicht nur effektiven Sonnenschutz, sondern auch
          zusätzliche Sicherheit, Isolierung und Verdunkelung. Sie sind ideal
          für Anwendungen, bei denen neben dem Sonnenschutz auch Sichtschutz,
          Einbruchhemmung oder thermische Isolierung gefragt sind.
          <h2>Bioklimatische Pergolen — Moderne Außenraumgestaltung</h2>
          Bioklimatische Pergolen verbinden Sonnenschutz mit Wetterschutz und
          schaffen wetterunabhängige Außenräume. Mit verstellbaren Lamellen
          ermöglichen sie eine natürliche Klimatisierung und bieten ganzjährigen
          Komfort für Terrassen und Außenbereiche.
          <h3>Glasdachsysteme — Transparente Überdachungen</h3>
          Glasdachsysteme bieten lichtdurchlässige Überdachungen für Terrassen,
          Wintergärten und andere Außenbereiche. Sie lassen Tageslicht herein,
          während sie gleichzeitig vor Regen und anderen Witterungseinflüssen
          schützen.
          <h1>Schiebeverglasungen — Flexible Raumöffnungen</h1>
          Schiebeverglasungen ermöglichen eine flexible Öffnung von Räumen zur
          Terrasse oder zum Garten. Sie bieten maximalen Lichteinfall und
          schaffen fließende Übergänge zwischen Innen- und Außenbereichen.
          <h2>Premium-Markisen — Hochwertige Terrassenbeschattung</h2>
          Premium-Markisen sind besonders robuste und langlebige
          Beschattungssysteme für hochwertige Ansprüche. Sie bieten exzellente
          Qualität, moderne Designs und zuverlässigen Schutz für anspruchsvolle
          Außenbereiche.
          <h3>Policarbonat-Dächer — Leichte und stabile Überdachungen</h3>
          Polycarbonat-Dächer sind leichte, stabile und transparente
          Überdachungen für Terrassen, Carports und andere Anwendungen. Sie
          bieten hervorragenden Wetterschutz bei gleichzeitig hoher
          Lichtdurchlässigkeit.`}
        </html>
      </Helmet>

      <div className="min-h-svh">
        {/* Full-Width About Section */}
        <motion.section
          className="w-full flex flex-col justify-center text-black px-smallPx md:px-mediumPx lg:px-largePx xl:px-[200px] pt-20 bg-cover bg-center rounded-lg"
          style={{
            backgroundImage: `url(${aboutbg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="w-full py-10">
            <h1 className="text-[42px] font-bold mb-4 text-white">
              {t('aboutScreen.title')}
            </h1>
            <p className=" text-white">
              {t('aboutScreen.description1', {
                company: 'Sonnen Raffstoren GmbH',
              })}{' '}
              <strong>{t('aboutScreen.strong1')}</strong>{' '}
              {t('aboutScreen.descriptionMiddle')}{' '}
              <strong>{t('aboutScreen.strong2')}</strong>{' '}
              {t('aboutScreen.description2')}
            </p>
          </div>
          <div className="flex w-full items-center justify-center py-10">
            <div className="flex flex-col md:flex-row bg-white/70 backdrop-blur p-10 gap-5 rounded-[5px] shadow-xl">
              <div className="flex flex-col items-center justify-center">
                <h1 className="text-[42px] font-[800]">
                  {countCustomerSatisfaction}%
                </h1>
                <p className=" font-[400]">
                  {t('aboutScreen.stats.customerSatisfaction')}
                </p>
              </div>

              {/* Vertical separator */}
              <div className="hidden md:block h-24 w-px bg-black" />
              <hr className="block md:hidden w-full h-[0.5px] border-black" />

              <div className="flex flex-col items-center justify-center">
                <h1 className="text-[42px] font-[800]">
                  {countEnergyEfficiency}%
                </h1>
                <p className=" font-[400]">
                  {t('aboutScreen.stats.energyEfficiency')}
                </p>
              </div>

              {/* Vertical separator */}
              <div className="hidden md:block h-24 w-px bg-black" />
              <hr className="block md:hidden w-full h-[0.5px] border-black" />

              <div className="flex flex-col items-center justify-center">
                <h1 className="text-[42px] font-[800]">
                  +{formatNumberWithSpaces(countBlindsInstalled)}
                </h1>
                <p className=" font-[400]">
                  {t('aboutScreen.stats.blindsInstalled')}
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="px-smallPx md:px-mediumPx lg:px-largePx xl:px-[200px] py-[40px]"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left column with text content */}
            <div>
              <h2 className="text-3xl font-bold mb-4">
                {t('aboutScreen.commitmentTitle')}
              </h2>
              <p className="text-gray-700 ">
                {t('aboutScreen.commitmentDescription')}
              </p>
              <p className="text-gray-700  mt-4">
                {t('aboutScreen.commitmentDescription2')}
              </p>
            </div>

            {/* Right column with list content */}
            <div>
              <h2 className="text-3xl font-bold mb-4">
                {t('aboutScreen.whyChooseTitle')}
              </h2>
              <ul className="list-disc ml-5 text-gray-700 ">
                <li className="m-0">
                  {t('aboutScreen.whyChooseList.innovativeDesigns')}
                </li>
                <li className="m-0">
                  {t('aboutScreen.whyChooseList.customBlinds')}
                </li>
                <li className="m-0">
                  {t('aboutScreen.whyChooseList.aestheticsEnergy')}
                </li>
                <li className="m-0">
                  {t('aboutScreen.whyChooseList.expertConsultation')}
                </li>
                <li className="m-0">
                  {t('aboutScreen.whyChooseList.reliableSupport')}
                </li>
              </ul>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="px-smallPx md:px-mediumPx lg:px-largePx xl:px-[200px] py-[100px]"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">
              {t('aboutScreen.comprehensiveSolutions.title')}
            </h2>
            <p className="text-gray-700 text-center mb-12 max-w-3xl mx-auto">
              {t('aboutScreen.comprehensiveSolutions.subtitle')}
            </p>

            <div className="embla" ref={emblaRef}>
              <div className="embla__container flex gap-5 py-5">
                <div className="embla__slide w-full lg:w-1/3">
                  {/* Bioclimatic Pergola */}
                  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-semibold mb-3 text-primary">
                      {t(
                        'aboutScreen.comprehensiveSolutions.bioclimaticPergola.title',
                      )}
                    </h3>
                    <p className="text-gray-600  mb-4">
                      {t(
                        'aboutScreen.comprehensiveSolutions.bioclimaticPergola.description',
                      )}
                    </p>
                    <ul className="text-gray-500 space-y-1">
                      {(() => {
                        const features = t(
                          'aboutScreen.comprehensiveSolutions.bioclimaticPergola.features',
                          { returnObjects: true },
                        );
                        return Array.isArray(features)
                          ? features.map((feature: any, index: number) => (
                              <li key={index}>• {String(feature)}</li>
                            ))
                          : [];
                      })()}
                    </ul>
                  </div>
                </div>

                <div className="embla__slide w-full lg:w-1/3">
                  {/* Glass Roof Systems */}
                  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-semibold mb-3 text-primary">
                      {t(
                        'aboutScreen.comprehensiveSolutions.glassRoofSystems.title',
                      )}
                    </h3>
                    <p className="text-gray-600  mb-4">
                      {t(
                        'aboutScreen.comprehensiveSolutions.glassRoofSystems.description',
                      )}
                    </p>
                    <ul className="text-gray-500  space-y-1">
                      {(() => {
                        const features = t(
                          'aboutScreen.comprehensiveSolutions.glassRoofSystems.features',
                          { returnObjects: true },
                        );
                        return Array.isArray(features)
                          ? features.map((feature: any, index: number) => (
                              <li key={index}>• {String(feature)}</li>
                            ))
                          : [];
                      })()}
                    </ul>
                  </div>
                </div>

                <div className="embla__slide w-full lg:w-1/3">
                  {/* Sliding Glass Systems */}
                  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-semibold mb-3 text-primary">
                      {t(
                        'aboutScreen.comprehensiveSolutions.slidingGlassSystems.title',
                      )}
                    </h3>
                    <p className="text-gray-600  mb-4">
                      {t(
                        'aboutScreen.comprehensiveSolutions.slidingGlassSystems.description',
                      )}
                    </p>
                    <ul className="text-gray-500  space-y-1">
                      {(() => {
                        const features = t(
                          'aboutScreen.comprehensiveSolutions.slidingGlassSystems.features',
                          { returnObjects: true },
                        );
                        return Array.isArray(features)
                          ? features.map((feature: any, index: number) => (
                              <li key={index}>• {String(feature)}</li>
                            ))
                          : [];
                      })()}
                    </ul>
                  </div>
                </div>

                <div className="embla__slide w-full lg:w-1/3">
                  {/* Premium Awnings */}
                  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-semibold mb-3 text-primary">
                      {t(
                        'aboutScreen.comprehensiveSolutions.premiumAwnings.title',
                      )}
                    </h3>
                    <p className="text-gray-600  mb-4">
                      {t(
                        'aboutScreen.comprehensiveSolutions.premiumAwnings.description',
                      )}
                    </p>
                    <ul className="text-gray-500  space-y-1">
                      {(() => {
                        const features = t(
                          'aboutScreen.comprehensiveSolutions.premiumAwnings.features',
                          { returnObjects: true },
                        );
                        return Array.isArray(features)
                          ? features.map((feature: any, index: number) => (
                              <li key={index}>• {String(feature)}</li>
                            ))
                          : [];
                      })()}
                    </ul>
                  </div>
                </div>

                <div className="embla__slide w-full lg:w-1/3">
                  {/* ZIP Screen Technology */}
                  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-semibold mb-3 text-primary">
                      {t(
                        'aboutScreen.comprehensiveSolutions.zipScreenTechnology.title',
                      )}
                    </h3>
                    <p className="text-gray-600  mb-4">
                      {t(
                        'aboutScreen.comprehensiveSolutions.zipScreenTechnology.description',
                      )}
                    </p>
                    <ul className="text-gray-500  space-y-1">
                      {(() => {
                        const features = t(
                          'aboutScreen.comprehensiveSolutions.zipScreenTechnology.features',
                          { returnObjects: true },
                        );
                        return Array.isArray(features)
                          ? features.map((feature: any, index: number) => (
                              <li key={index}>• {String(feature)}</li>
                            ))
                          : [];
                      })()}
                    </ul>
                  </div>
                </div>

                <div className="embla__slide w-full lg:w-1/3">
                  {/* Polycarbonate Solutions */}
                  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow mr-5">
                    <h3 className="text-xl font-semibold mb-3 text-primary">
                      {t(
                        'aboutScreen.comprehensiveSolutions.polycarbonateRoofing.title',
                      )}
                    </h3>
                    <p className="text-gray-600  mb-4">
                      {t(
                        'aboutScreen.comprehensiveSolutions.polycarbonateRoofing.description',
                      )}
                    </p>
                    <ul className="text-gray-500  space-y-1">
                      {(() => {
                        const features = t(
                          'aboutScreen.comprehensiveSolutions.polycarbonateRoofing.features',
                          { returnObjects: true },
                        );
                        return Array.isArray(features)
                          ? features.map((feature: any, index: number) => (
                              <li key={index}>• {String(feature)}</li>
                            ))
                          : [];
                      })()}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="embla__dots">
              {scrollSnaps.map((_, index) => (
                <DotButton
                  key={index}
                  selected={index === selectedIndex}
                  onClick={() => emblaApi?.scrollTo(index)}
                />
              ))}
            </div>
          </div>
        </motion.section>
      </div>
    </>
  );
};
